import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface TrialStatus {
  has_trial: boolean;
  trial_id?: string;
  trial_status?: 'active' | 'grace' | 'expired' | 'converted' | 'cancelled';
  trial_start_at?: string;
  trial_end_at?: string;
  days_remaining?: number;
  trial_benefits_consumed?: boolean;
  trial_benefits_consumed_at?: string;
  subscribed_during_trial?: boolean;
  paid_plan_selected_at?: string;
  paid_subscription_starts_at?: string;
  first_billing_cycle_starts_at?: string;
  converted_to_plan_id?: string;
  daily_caps?: Record<string, number>;
  today_usage?: Record<string, number>;
  grace_enabled?: boolean;
  grace_duration_days?: number;
  /** Remaining trial days added on top of paid plan period */
  trial_extra_days?: number;
  /** Per-service remaining trial allowances added on top of plan limits */
  trial_extra_allowances?: Record<string, number>;
}

/**
 * Hook to get the current ForkHire trial status for the authenticated org.
 */
export function useTrialStatus() {
  const { profile } = useAuth();
  const orgId = profile?.org_id;

  return useQuery<TrialStatus>({
    queryKey: ['trial-status', orgId],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('manage-trial', {
        body: {
          action: 'status',
          platform: 'forkhire',
          owner_type: 'organization',
          owner_id: orgId,
        },
      });
      if (error) {
        console.warn('[useTrialStatus] Error:', error.message);
        return { has_trial: false };
      }
      return data as TrialStatus;
    },
    enabled: !!orgId,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });
}

/** Helper: is the org currently in an active trial? */
export function isTrialActive(status: TrialStatus | undefined): boolean {
  if (!status?.has_trial) return false;
  return status.trial_status === 'active' && !status.trial_benefits_consumed;
}

/** Helper: has the org exhausted trial benefits? */
export function isTrialBenefitsExhausted(status: TrialStatus | undefined): boolean {
  if (!status?.has_trial) return false;
  return status.trial_benefits_consumed === true;
}

/** Helper: did the org subscribe during trial? */
export function isSubscribedDuringTrial(status: TrialStatus | undefined): boolean {
  if (!status?.has_trial) return false;
  return status.subscribed_during_trial === true;
}

/** Helper: does the org have active trial extras on top of their plan? */
export function hasTrialExtras(status: TrialStatus | undefined): boolean {
  if (!status?.has_trial) return false;
  if (status.trial_benefits_consumed) return false;
  if (!status.subscribed_during_trial) return false;
  const extras = status.trial_extra_allowances ?? {};
  return Object.values(extras).some(v => v > 0) || (status.trial_extra_days ?? 0) > 0;
}

/**
 * Get the combined limit for a service: plan limit + trial extra.
 * Returns { planLimit, trialExtra, combinedLimit }.
 */
export function getCombinedServiceLimit(
  planLimit: number,
  serviceKey: string,
  trialStatus: TrialStatus | undefined,
): { planLimit: number; trialExtra: number; combinedLimit: number } {
  const trialExtra = hasTrialExtras(trialStatus)
    ? (trialStatus?.trial_extra_allowances?.[serviceKey] ?? 0)
    : 0;
  return {
    planLimit,
    trialExtra,
    combinedLimit: planLimit + trialExtra,
  };
}

/**
 * Get the effective first renewal date accounting for trial extra days.
 * Renewal = paid_subscription_starts_at + plan_period_days + trial_extra_days
 */
export function getEffectiveRenewalDate(
  paidStartDate: string | undefined,
  planPeriodDays: number,
  trialStatus: TrialStatus | undefined,
): Date | null {
  if (!paidStartDate) return null;
  const start = new Date(paidStartDate);
  const extraDays = hasTrialExtras(trialStatus) ? (trialStatus?.trial_extra_days ?? 0) : 0;
  const totalDays = planPeriodDays + extraDays;
  start.setDate(start.getDate() + totalDays);
  return start;
}
